<!-- views/Gallery.vue -->
<!-- Phase 2: Perfect例表示・検索ページ（完全最終版） -->

<template>
  <div class="gallery-page">
    <!-- ヘッダー -->
    <div class="page-header">
      <h1>🌟 Perfect Gallery</h1>
      <p>みんなのPerfect（100点）発音を聞いて、参考にしましょう！</p>
    </div>

    <!-- 検索・フィルター -->
    <div class="search-section">
      <div class="search-bar">
        <input 
          v-model="searchQuery"
          @input="debouncedSearch"
          type="text" 
          placeholder="文を検索..."
          class="search-input"
        >
        <div class="search-icon">🔍</div>
      </div>
      
      <div class="filters">
        <select v-model="selectedCategory" @change="applyFilters" class="filter-select">
          <option value="">全カテゴリ</option>
          <option value="basic">基礎</option>
          <option value="intermediate">中級</option>
          <option value="advanced">上級</option>
        </select>
        
        <select v-model="selectedPeriod" @change="applyFilters" class="filter-select">
          <option value="">全期間</option>
          <option value="week">1週間</option>
          <option value="month">1ヶ月</option>
          <option value="year">1年</option>
        </select>
        
        <select v-model="sortBy" @change="applyFilters" class="filter-select">
          <option value="newest">新しい順</option>
          <option value="popular">人気順</option>
          <option value="random">ランダム</option>
        </select>
      </div>
    </div>

    <!-- ローディング -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>Perfect例を読み込み中...</p>
    </div>

    <!-- エラー表示 -->
    <div v-else-if="error" class="error-container">
      <h3>❌ エラーが発生しました</h3>
      <p>{{ error }}</p>
      <button @click="loadResults" class="retry-button">再試行</button>
    </div>

    <!-- 結果表示 -->
    <div v-else class="gallery-content">
      <!-- 結果統計 -->
      <div class="results-stats">
        <span>{{ filteredResults.length }}件のPerfect例</span>
        <span v-if="searchQuery">「{{ searchQuery }}」の検索結果</span>
      </div>

      <!-- ギャラリーグリッド -->
      <div v-if="filteredResults.length > 0" class="gallery-grid">
        <div 
          v-for="result in paginatedResults" 
          :key="result.id"
          class="gallery-card"
        >
          <div class="card-header">
            <h3 class="sentence">{{ truncateSentence(result.sentence) }}</h3>
            <div class="perfect-badge">Perfect</div>
          </div>
          
          <div class="card-content">
            <audio 
              :src="result.audio_url" 
              controls
              preload="metadata"
              @play="incrementPlayCount(result.id)"
              @loadedmetadata="onAudioLoaded(result.id)"
              class="audio-player"
            ></audio>
            
            <div class="card-meta">
              <div class="user-info">
                <span class="nickname">{{ result.nickname || '匿名' }}</span>
                <span class="date">{{ formatDate(result.created_at) }}</span>
              </div>
              <div class="stats">
                <span class="play-count">👂 {{ result.play_count }}</span>
                <span class="category">{{ getCategoryName(result.training_tasks?.category) }}</span>
              </div>
            </div>
            
            <p class="ai-comment">{{ result.ai_comment }}</p>
            
            <div class="card-actions">
              <button 
                @click="goToTraining(result.task_id)"
                class="action-button"
              >
                /training に戻る
              </button>
              <button 
                @click="reportContent(result.id)"
                class="report-button"
              >
                通報
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空の状態 -->
      <div v-else class="empty-state">
        <div class="empty-icon">🎵</div>
        <h3>Perfect例が見つかりません</h3>
        <p v-if="searchQuery">「{{ searchQuery }}」に一致するPerfect例はありません</p>
        <p v-else>まだPerfect例がありません。最初のPerfectを目指しましょう！</p>
        <button @click="goToTraining" class="go-training-button">
          /training で練習する
        </button>
      </div>
      
      <!-- ページネーション -->
      <div class="pagination" v-if="totalPages > 1">
        <button 
          @click="previousPage" 
          :disabled="currentPage === 1"
          class="page-button"
        >
          ← 前へ
        </button>
        
        <div class="page-numbers">
          <button 
            v-for="page in visiblePages" 
            :key="page"
            @click="goToPage(page)"
            :class="{ active: page === currentPage }"
            class="page-number"
          >
            {{ page }}
          </button>
        </div>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage === totalPages"
          class="page-button"
        >
          次へ →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'

const router = useRouter()
const route = useRoute()

// 状態
const isLoading = ref(true)
const error = ref(null)
const results = ref([])
const filteredResults = ref([])
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedPeriod = ref('')
const sortBy = ref('newest')
const currentPage = ref(1)
const itemsPerPage = 12

// 計算プロパティ
const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredResults.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredResults.value.length / itemsPerPage)
})

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, start + 4)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// ライフサイクル
onMounted(async () => {
  await loadResults()
  
  // クエリパラメータから検索条件を復元
  const query = route.query
  if (query.search) searchQuery.value = query.search
  if (query.category) selectedCategory.value = query.category
  if (query.period) selectedPeriod.value = query.period
  if (query.sort) sortBy.value = query.sort
  
  applyFilters()
})

// 検索のデバウンス
let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 300)
}

// メソッド
const loadResults = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    // JOIN修正版（リレーション名を明示）
    const { data, error: searchError } = await supabase
      .from('training_results')
      .select(`
        id,
        sentence,
        audio_url,
        ai_comment,
        nickname,
        play_count,
        created_at,
        task_id,
        training_tasks(category)
      `)
      .eq('shared', true)
      .eq('score', 100)
      .order('created_at', { ascending: false })
      .limit(1000)
    
    if (searchError) throw searchError
    
    results.value = data || []
    filteredResults.value = [...results.value]
    
    console.log('✅ ギャラリーデータ読み込み完了:', results.value.length, '件')
    
  } catch (err) {
    console.error('ギャラリーデータ読み込み失敗:', err)
    error.value = err.message || 'データの読み込みに失敗しました'
  } finally {
    isLoading.value = false
  }
}

const applyFilters = () => {
  let filtered = [...results.value]
  
  // 検索フィルタ
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(result => 
      result.sentence.toLowerCase().includes(query)
    )
  }
  
  // カテゴリフィルタ（JOIN修正版）
  if (selectedCategory.value) {
    filtered = filtered.filter(result => 
      result.training_tasks?.category === selectedCategory.value
    )
  }
  
  // 期間フィルタ
  if (selectedPeriod.value) {
    const now = new Date()
    let startDate
    
    switch (selectedPeriod.value) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'year':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
        break
    }
    
    if (startDate) {
      filtered = filtered.filter(result => 
        new Date(result.created_at) >= startDate
      )
    }
  }
  
  // ソート
  switch (sortBy.value) {
    case 'newest':
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      break
    case 'popular':
      filtered.sort((a, b) => b.play_count - a.play_count)
      break
    case 'random':
      filtered.sort(() => Math.random() - 0.5)
      break
  }
  
  filteredResults.value = filtered
  currentPage.value = 1
  
  // URLを更新
  updateURL()
}

const updateURL = () => {
  const query = {}
  if (searchQuery.value) query.search = searchQuery.value
  if (selectedCategory.value) query.category = selectedCategory.value
  if (selectedPeriod.value) query.period = selectedPeriod.value
  if (sortBy.value !== 'newest') query.sort = sortBy.value
  
  router.replace({ query })
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const goToPage = (page) => {
  currentPage.value = page
}

const incrementPlayCount = async (resultId) => {
  try {
    await supabase.rpc('increment_play_count', { p_result_id: resultId })
    
    // ローカルの再生回数を更新
    const result = results.value.find(r => r.id === resultId)
    if (result) {
      result.play_count++
    }
    
  } catch (error) {
    console.error('再生回数更新失敗:', error)
  }
}

const onAudioLoaded = (resultId) => {
  console.log('音声読み込み完了:', resultId)
}

const reportContent = async (resultId) => {
  try {
    const reason = prompt('通報理由を入力してください:')
    if (!reason) return
    
    const { error } = await supabase.rpc('create_report', {
      p_target_type: 'training_result',
      p_target_id: resultId,
      p_reason: reason,
      p_description: ''
    })
    
    if (error) throw error
    
    alert('通報を受け付けました。ご協力ありがとうございます。')
    
  } catch (error) {
    console.error('通報失敗:', error)
    alert('通報に失敗しました。')
  }
}

const goToTraining = (taskId) => {
  if (taskId) {
    // 引数がある場合：特定タスクに遷移
    router.push({
      name: 'TrainingTask',
      params: { taskId }
    })
  } else {
    // 引数がない場合：練習トップに遷移
    router.push('/training')
  }
}

const truncateSentence = (sentence) => {
  return sentence.length > 50 ? sentence.substring(0, 50) + '...' : sentence
}

const getCategoryName = (category) => {
  const names = {
    basic: '基礎',
    intermediate: '中級',
    advanced: '上級'
  }
  return names[category] || category
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })
}
</script>

<style scoped>
.gallery-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
  color: white;
}

.page-header h1 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-header p {
  font-size: 1.1rem;
  opacity: 0.9;
}

.search-section {
  max-width: 1200px;
  margin: 0 auto 3rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 2rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.search-bar {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-input {
  width: 100%;
  padding: 1rem 3rem 1rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-input:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  color: #6b7280;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-select {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  background: white;
  color: #1f2937;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-select:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  color: white;
  background: rgba(239, 68, 68, 0.1);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.retry-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.gallery-content {
  max-width: 1200px;
  margin: 0 auto;
}

.results-stats {
  color: white;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  opacity: 0.9;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.gallery-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gallery-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.card-header {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 1.1rem;
  flex: 1;
}

.perfect-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
}

.card-content {
  padding: 1.5rem;
}

.audio-player {
  width: 100%;
  margin-bottom: 1rem;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #6b7280;
}

.user-info {
  display: flex;
  gap: 1rem;
}

.nickname {
  font-weight: bold;
  color: #1f2937;
}

.stats {
  display: flex;
  gap: 1rem;
}

.ai-comment {
  font-style: italic;
  color: #4b5563;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.card-actions {
  display: flex;
  gap: 1rem;
}

.action-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  flex: 1;
  transition: all 0.2s;
}

.action-button:hover {
  background: #2563eb;
}

.report-button {
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.report-button:hover {
  background: #4b5563;
}

.empty-state {
  text-align: center;
  color: white;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  opacity: 0.9;
  margin-bottom: 2rem;
}

.go-training-button {
  background: #10b981;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.go-training-button:hover {
  background: #059669;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
}

.page-button {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
}

.page-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-number {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 44px;
}

.page-number:hover {
  background: rgba(255, 255, 255, 0.3);
}

.page-number.active {
  background: white;
  color: #1f2937;
}

@media (max-width: 768px) {
  .gallery-page {
    padding: 1rem;
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .search-section {
    padding: 1rem;
  }
  
  .filters {
    flex-direction: column;
  }
  
  .gallery-grid {
    grid-template-columns: 1fr;
  }
  
  .card-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .card-actions {
    flex-direction: column;
  }
  
  .pagination {
    flex-wrap: wrap;
  }
}
</style>
